import { useState } from 'react'
import { ListChecks, Loader2, Check, X } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'

import { Button } from '@/common/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/common/ui/sheet'
import { ScrollArea } from '@/common/ui/scroll-area'

import {
	useQueryAdminTasks,
	useMutationAdminTaskDone,
	useMutationAdminTaskDismiss,
} from '../../../api/hooks'
import type { AdminTaskResponse } from '../../../api/services'

interface ChatAdminTasksMobileSheetProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	roomId: string
}

export function ChatAdminTasksMobileSheet({
	open,
	onOpenChange,
	roomId,
}: ChatAdminTasksMobileSheetProps) {
	const { data: tasks, isLoading } = useQueryAdminTasks(roomId, open)
	const doneMutation = useMutationAdminTaskDone(roomId)
	const dismissMutation = useMutationAdminTaskDismiss(roomId)
	const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())

	const markPending = (id: string, isPending: boolean) => {
		setPendingIds((prev) => {
			const next = new Set(prev)
			if (isPending) next.add(id)
			else next.delete(id)
			return next
		})
	}

	const handleDone = async (task: AdminTaskResponse) => {
		markPending(task.id, true)
		try {
			await doneMutation.mutateAsync(task.id)
		} finally {
			markPending(task.id, false)
		}
	}

	const handleDismiss = async (task: AdminTaskResponse) => {
		markPending(task.id, true)
		try {
			await dismissMutation.mutateAsync(task.id)
		} finally {
			markPending(task.id, false)
		}
	}

	const items = tasks ?? []

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="w-full p-0 sm:max-w-md">
				<SheetHeader className="border-b px-6 py-4">
					<SheetTitle className="flex items-center gap-2">
						<ListChecks className="h-5 w-5 text-amber-500" />
						แจ้งเตือน
						{items.length > 0 && (
							<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900 dark:text-amber-200">
								{items.length}
							</span>
						)}
					</SheetTitle>
				</SheetHeader>

				<div className="h-[calc(100%-65px)]">
					{isLoading ? (
						<div className="flex h-full items-center justify-center">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						</div>
					) : items.length === 0 ? (
						<div className="flex h-full flex-col items-center justify-center px-6 text-center text-muted-foreground">
							<ListChecks className="mb-2 h-12 w-12 opacity-40" />
							<p className="text-sm font-medium">ไม่มีแจ้งเตือน</p>
							<p className="text-xs">
								AI
								จะเพิ่มแจ้งเตือนอัตโนมัติเมื่อมีข้อความใหม่ที่ต้องแอดมินจัดการ
							</p>
						</div>
					) : (
						<ScrollArea className="h-full">
							<div className="space-y-2 p-4">
								{items.map((task) => {
									const isPending = pendingIds.has(task.id)
									return (
										<div
											key={task.id}
											className="rounded-lg border bg-card p-3"
										>
											<p className="mb-2 whitespace-pre-wrap text-sm leading-relaxed">
												{task.text}
											</p>
											<div className="flex items-center justify-between gap-2">
												<span className="text-[10px] text-muted-foreground">
													{formatDistanceToNow(new Date(task.createdAt), {
														addSuffix: true,
														locale: th,
													})}
												</span>
												<div className="flex gap-1">
													<Button
														variant="outline"
														size="sm"
														className="h-7 gap-1 border-emerald-500 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-950"
														disabled={isPending}
														onClick={() => handleDone(task)}
													>
														{isPending ? (
															<Loader2 className="h-3 w-3 animate-spin" />
														) : (
															<Check className="h-3 w-3" />
														)}
														<span className="text-xs">ทำแล้ว</span>
													</Button>
													<Button
														variant="ghost"
														size="sm"
														className="h-7 gap-1 text-muted-foreground hover:bg-muted hover:text-foreground"
														disabled={isPending}
														onClick={() => handleDismiss(task)}
													>
														<X className="h-3 w-3" />
														<span className="text-xs">ไม่ทำ</span>
													</Button>
												</div>
											</div>
										</div>
									)
								})}
							</div>
						</ScrollArea>
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}
